import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'

import { ApiError } from '@/api/client'
import { DataTable, type Column } from '@/components/DataTable'
import { ExportButton } from '@/components/ExportButton'
import { Alert } from '@/components/ui/Alert'
import { Button } from '@/components/ui/Button'
import { Card, CardHeader } from '@/components/ui/Card'
import { Modal } from '@/components/ui/Modal'
import { useAuth } from '@/features/auth/store'
import { DepartmentFormDialog } from '@/features/employees/DepartmentFormDialog'
import { EmployeeFormDialog } from '@/features/employees/EmployeeFormDialog'
import {
  EMPLOYEE_STATUSES,
  deleteDepartment,
  deleteEmployee,
  exportEmployees,
  listDepartments,
  listEmployees,
  type DepartmentRead,
  type EmployeeFilters,
  type EmployeeRead,
} from '@/features/employees/api'
import { toast } from '@/lib/toast'

const SELECT_CLASS =
  'h-10 rounded-md border border-slate-300 bg-white px-3 text-sm text-slate-900 ' +
  'focus:border-brand-500 focus:outline-none focus:ring-1 focus:ring-brand-500 ' +
  'dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100'

const STATUS_TONE: Record<string, string> = {
  active: 'bg-success/10 text-success',
  on_leave: 'bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200',
  terminated: 'bg-slate-200 text-slate-600 dark:bg-slate-700 dark:text-slate-300',
}

const employeeColumns = (
  canEdit: boolean,
  onEdit: (employee: EmployeeRead) => void,
): Column<EmployeeRead>[] => [
  {
    key: 'employee_code',
    header: 'Code',
    className: 'font-mono text-xs',
    sortValue: (employee) => employee.employee_code,
    cell: (employee) =>
      canEdit ? (
        <button type="button" onClick={() => onEdit(employee)} className="hover:underline">
          {employee.employee_code}
        </button>
      ) : (
        employee.employee_code
      ),
  },
  {
    key: 'full_name',
    header: 'Name',
    className: 'font-medium',
    sortValue: (employee) => employee.full_name ?? '',
    cell: (employee) => employee.full_name ?? '—',
  },
  {
    key: 'position',
    header: 'Position',
    cell: (employee) => employee.position,
  },
  {
    key: 'department_name',
    header: 'Department',
    className: 'text-slate-500 dark:text-slate-400',
    cell: (employee) => employee.department_name ?? '—',
  },
  {
    key: 'status',
    header: 'Status',
    cell: (employee) => (
      <span className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_TONE[employee.status] ?? STATUS_TONE.terminated}`}>
        {employee.status.replace('_', ' ')}
      </span>
    ),
  },
]

/**
 * Departments as a small table beneath the employee list -- the same "read here, edit
 * through a dialog" shape as the accounts screen's posting rules, except this one is
 * write-enabled since departments have no other home in the app.
 */
function DepartmentsPanel({ canWrite }: { canWrite: boolean }) {
  const queryClient = useQueryClient()
  const [dialog, setDialog] = useState<{ department?: DepartmentRead } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<DepartmentRead | null>(null)

  const { data, isLoading, error } = useQuery({
    queryKey: ['departments', ''],
    queryFn: () => listDepartments(),
    retry: false,
  })

  const remove = useMutation({
    mutationFn: (department: DepartmentRead) => deleteDepartment(department.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['departments'] })
      toast.success('Department removed.')
      setConfirmDelete(null)
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof ApiError ? error.message : 'Could not reach the server. Nothing was changed.',
      )
      setConfirmDelete(null)
    },
  })

  const departments = data?.data ?? []

  return (
    <Card>
      <CardHeader
        title="Departments"
        description="Who reports where. Deleting one clears it from every employee that pointed at it."
        action={canWrite ? <Button size="sm" onClick={() => setDialog({})}>New department</Button> : undefined}
      />
      <DataTable
        rows={departments}
        columns={[
          {
            key: 'name',
            header: 'Name',
            className: 'font-medium',
            cell: (department) =>
              canWrite ? (
                <button
                  type="button"
                  onClick={() => setDialog({ department })}
                  className="text-left hover:underline"
                >
                  {department.name}
                </button>
              ) : (
                department.name
              ),
          },
          {
            key: 'description',
            header: 'Description',
            className: 'text-slate-500 dark:text-slate-400',
            cell: (department) => department.description ?? '—',
          },
          ...(canWrite
            ? [
                {
                  key: 'actions',
                  header: '',
                  headerClassName: 'text-right',
                  className: 'text-right',
                  cell: (department: DepartmentRead) => (
                    <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(department)}>
                      Delete
                    </Button>
                  ),
                } satisfies Column<DepartmentRead>,
              ]
            : []),
        ]}
        getRowId={(department) => department.id}
        caption="Departments"
        isLoading={isLoading}
        error={error}
        emptyMessage="No departments yet."
      />

      {dialog && (
        <DepartmentFormDialog open department={dialog.department} onClose={() => setDialog(null)} />
      )}

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title={confirmDelete ? `Delete ${confirmDelete.name}?` : 'Delete department'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => confirmDelete && remove.mutate(confirmDelete)}
            >
              Delete
            </Button>
          </>
        }
      >
        <Alert tone="info">
          Any employee assigned to this department keeps their record; only the department
          link clears.
        </Alert>
      </Modal>
    </Card>
  )
}

/**
 * The employee master. Names lead to an edit dialog rather than a detail page -- the same
 * shape the chart of accounts uses -- since there is no ledger-like drill-down behind a
 * person the way there is behind an account or a product.
 */
export function Employees() {
  const can = useAuth((s) => s.can)
  const queryClient = useQueryClient()
  const [filters, setFilters] = useState<EmployeeFilters>({ page: 1, status: 'active' })
  const [search, setSearch] = useState('')
  const [dialog, setDialog] = useState<{ employee?: EmployeeRead } | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<EmployeeRead | null>(null)

  const { data, isLoading, isFetching, error } = useQuery({
    queryKey: ['employees', filters],
    queryFn: () => listEmployees(filters),
    retry: false,
  })

  const canCreate = can('performance_reviews', 'create')
  const canUpdate = can('performance_reviews', 'update')
  const canDelete = can('performance_reviews', 'delete')

  const remove = useMutation({
    mutationFn: (employee: EmployeeRead) => deleteEmployee(employee.id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['employees'] })
      toast.success('Employee removed.')
      setConfirmDelete(null)
    },
    onError: (error: unknown) => {
      toast.error(
        error instanceof ApiError ? error.message : 'Could not reach the server. Nothing was changed.',
      )
      setConfirmDelete(null)
    },
  })

  const employees = data?.data ?? []
  const meta = data?.meta

  const set = (patch: Partial<EmployeeFilters>) =>
    setFilters((current) => ({ ...current, ...patch, page: 1 }))

  const columns: Column<EmployeeRead>[] = [
    ...employeeColumns(canUpdate, (employee) => setDialog({ employee })),
    ...(canDelete
      ? [
          {
            key: 'actions',
            header: '',
            headerClassName: 'text-right',
            className: 'text-right',
            cell: (employee: EmployeeRead) => (
              <Button variant="ghost" size="sm" onClick={() => setConfirmDelete(employee)}>
                Remove
              </Button>
            ),
          } satisfies Column<EmployeeRead>,
        ]
      : []),
  ]

  return (
    <div className="space-y-4">
      <header className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight">Employees</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            {meta ? `${meta.total} employee${meta.total === 1 ? '' : 's'}` : 'Loading…'}
          </p>
        </div>
        {canCreate && <Button onClick={() => setDialog({})}>New employee</Button>}
      </header>

      <Card>
        <CardHeader
          title="Directory"
          description="Salary, tax ID and bank details are hidden here unless you're HR, Super Admin, or looking at your own record."
        />
        <DataTable
          rows={employees}
          columns={columns}
          getRowId={(employee) => employee.id}
          caption="Employees"
          isLoading={isLoading}
          error={error}
          emptyMessage="No employees match these filters."
          toolbar={
            <div className="flex flex-wrap items-center gap-2">
              <label className="sr-only" htmlFor="employee-search">
                Search employees
              </label>
              <input
                id="employee-search"
                className={SELECT_CLASS}
                placeholder="Code or position…"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') set({ q: search })
                }}
                onBlur={() => set({ q: search })}
              />

              <label className="sr-only" htmlFor="employee-status">
                Status
              </label>
              <select
                id="employee-status"
                className={SELECT_CLASS}
                value={filters.status ?? ''}
                onChange={(event) => set({ status: event.target.value })}
              >
                <option value="">Any status</option>
                {EMPLOYEE_STATUSES.map((status) => (
                  <option key={status} value={status}>
                    {status.replace('_', ' ')}
                  </option>
                ))}
              </select>

              {(filters.q || filters.status !== 'active') && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    setSearch('')
                    setFilters({ page: 1, status: 'active' })
                  }}
                >
                  Clear
                </Button>
              )}

              <ExportButton
                filename="employees"
                selectClassName={SELECT_CLASS}
                onExport={(format) => exportEmployees(filters, format)}
                errorMessage="Could not export the directory."
              />
            </div>
          }
          pagination={
            meta
              ? {
                  page: meta.page,
                  totalPages: meta.total_pages,
                  onPage: (page) => setFilters((current) => ({ ...current, page })),
                  isFetching,
                }
              : undefined
          }
        />
      </Card>

      <DepartmentsPanel canWrite={canCreate} />

      {dialog && (
        <EmployeeFormDialog open employee={dialog.employee} onClose={() => setDialog(null)} />
      )}

      <Modal
        open={confirmDelete !== null}
        onClose={() => setConfirmDelete(null)}
        title={confirmDelete ? `Remove ${confirmDelete.employee_code}?` : 'Remove employee'}
        footer={
          <>
            <Button variant="secondary" onClick={() => setConfirmDelete(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              loading={remove.isPending}
              onClick={() => confirmDelete && remove.mutate(confirmDelete)}
            >
              Remove
            </Button>
          </>
        }
      >
        <Alert tone="info">
          This is a soft delete: the record is marked terminated and hidden from new reviews,
          but every past review and payroll figure against it stays exactly as it was.
        </Alert>
      </Modal>
    </div>
  )
}
